import React from 'react';

import { cn } from '@/lib/utils';

export interface TableBasicInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur' | 'value' | 'defaultValue'
> {
  /** Current value (used as defaultValue for uncontrolled input) */
  value?: string | number | null;
  /** Callback when value changes (called on blur) */
  onChange?: (value: string | null) => void;
  /** Custom onBlur handler (called after onChange) */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /** Error state styling */
  hasError?: boolean;
  /** Text alignment */
  align?: 'left' | 'right' | 'center';
  /** Padding right (for suffix icons like €, %) */
  paddingRight?: number;
  /** Custom onChange handler for real-time validation (optional) */
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Custom value transformation on blur */
  transformValue?: (value: string) => string | null;
}

/**
 * Base input component for table inline editing
 * Provides common structure, styles, and behavior for all table inputs
 * Uses native <input> instead of Input component to avoid style conflicts
 */
export const TableBasicInput = React.forwardRef<
  HTMLInputElement,
  TableBasicInputProps
>(
  (
    {
      value,
      onChange,
      onBlur,
      onKeyDown,
      disabled,
      autoFocus,
      className,
      hasError = false,
      align = 'left',
      paddingRight,
      onInputChange,
      transformValue,
      ...props
    },
    ref
  ) => {
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const trimmedValue = e.target.value.trim();

      // Apply custom transformation if provided
      const finalValue = transformValue
        ? transformValue(trimmedValue)
        : trimmedValue === ''
          ? null
          : trimmedValue;

      // Only call onChange if value actually changed
      if (onChange) {
        const currentValueStr =
          value === null || value === undefined ? '' : String(value);
        const newValueStr = finalValue ?? '';
        if (currentValueStr !== newValueStr) {
          onChange(finalValue);
        }
      }

      // Call custom onBlur handler
      onBlur?.(e);
    };

    const paddingClass =
      paddingRight !== undefined
        ? undefined // Will use inline style
        : align === 'right'
          ? 'pr-6'
          : 'px-2';

    const inputClasses = cn(
      'h-full w-full rounded-none bg-transparent text-sm',
      'overflow-x-auto overflow-y-hidden whitespace-nowrap',
      'focus-visible:outline-none',
      hasError
        ? 'focus-visible:border-red-500 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-red-500'
        : 'focus-visible:border-black focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-black',
      'focus-visible:ring-offset-0',
      'disabled:cursor-not-allowed disabled:opacity-50',
      align === 'right' && 'text-right',
      align === 'center' && 'text-center',
      paddingClass,
      className
    );

    const inputStyle =
      paddingRight !== undefined
        ? { paddingRight: `${paddingRight * 0.25}rem` } // Convert to rem (6 = 1.5rem)
        : undefined;

    return (
      <div
        className="relative flex h-full w-full items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={ref}
          type="text"
          defaultValue={
            value === null || value === undefined ? '' : String(value)
          }
          className={inputClasses}
          style={inputStyle}
          autoFocus={autoFocus}
          onKeyDown={onKeyDown}
          onBlur={handleBlur}
          onChange={onInputChange}
          disabled={disabled}
          {...props}
        />
      </div>
    );
  }
);

TableBasicInput.displayName = 'TableBasicInput';
